import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../hooks/ThemeContext';
import { useConfig } from '../../hooks/ConfigProvider'
import { HiSearch, HiLocationMarker, HiTruck } from 'react-icons/hi';
import { GiNoodles, GiBowlOfRice, GiHamburger, GiTacos, GiPizzaSlice, GiChickenLeg, GiKnifeFork } from 'react-icons/gi';
import { PostcodeModal, LocationBar } from './PostcodeComponents';
import './RestaurantPageStyles.css';
import PostcodeService from '../../service/PostcodeService';
import TimeService from '../../service/TimeService';
import { PostcodeServiceFactory } from '../../service/PostcodeServiceFactory';

interface Restaurant {
    name: string;
    general?: any;
    searchTerms?: string;
    featuredImageUrl: string;
    postcodes: string[];
    delivery: {
      enabled: boolean;
      showEstimatedTime?: boolean;
      radius: number,
      estimationRule?: {
        cookingTime: number;   // Base cooking time in minutes
        minsPerMile: number;   // Minutes per mile for delivery
        extraTime: number;     // Additional buffer time in minutes
      };
    };
    pickup?: {
      enabled: boolean;
    };
    dinein?: {
      enabled: boolean;
    };
}
  
interface DeliveryInfo {
    canDeliver: boolean;
    distance?: number;
    estimatedTime?: number;
    nearestLocation?: string;
    message?: string;
}
  
  interface RestaurantDeliveryInfo {
    [restaurantId: string]: DeliveryInfo;
  }

const CUISINE_TYPES = [
  { icon: GiHamburger, name: 'Burgers', color: '#FF7043' },
  { icon: GiPizzaSlice, name: 'Pizza', color: '#FF5252' },
  { icon: GiNoodles, name: 'Asian', color: '#7C4DFF' },
  { icon: GiBowlOfRice, name: 'Indian', color: '#FF6D00' },
  { icon: GiTacos, name: 'Mexican', color: '#43A047' },
  { icon: GiChickenLeg, name: 'Chicken', color: '#FB8C00' },
];

const RestaurantsPage: React.FC = () => {
    console.log("=== RESTAURANTSPAGE RENDER ===");

  const navigate = useNavigate();
  const { theme } = useTheme();
  console.log("getting config for restaurantspage")
  const { config } = useConfig();
  const restaurants = config?.restaurants as Record<string, Restaurant>;
  const featuredStores = config?.properties?.featuredStores ?? [];
  const [availableRestaurants, setAvailableRestaurants] = useState<Record<string, Restaurant>>({});
  const showCategories = config?.properties?.showCategories || false;
  const showFeatured = config?.properties?.showFeatured || false;
  const disableThumbnails = config?.properties?.disableThumbnails || true;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCuisine, setSelectedCuisine] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showPostcodeModal, setShowPostcodeModal] = useState(false);
  const [postcode, setPostcode] = useState('');
  const [restaurantDeliveryInfo, setRestaurantDeliveryInfo] = useState<RestaurantDeliveryInfo>({});
  const [isAreaServed, setIsAreaServed] = useState<boolean | null>(null);

    console.log("config:", config);
  console.log("config.restaurants:", config?.restaurants);


  useEffect(() => {
    const filterAvailable = async () => {
      const filtered: Record<string, Restaurant> = {};
      
      for (const [id, restaurant] of Object.entries(restaurants)) {
        if (await checkRestaurantAvailable(restaurant)) {
          filtered[id] = restaurant;
        }
      }
      
      setAvailableRestaurants(filtered);
    };
   
    filterAvailable();
   }, [restaurants]);

  useEffect(() => {
    const storedPostcode = localStorage.getItem('userPostcode');
    if (!storedPostcode) {
      setShowPostcodeModal(true);
    } else {
      setPostcode(storedPostcode);
    }

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const calculateRestaurantDeliveryTimes = async () => {
      if (!postcode) return;
      
      const postcodeService =  PostcodeServiceFactory.getPostcodeService();
      const newDeliveryInfo: RestaurantDeliveryInfo = {};
      let anyRestaurantCanDeliver = false;
  
      for (const [restaurantId, restaurant] of Object.entries(restaurants)) {
        try {
          const validation = await postcodeService.validateDeliveryLocation(
            postcode,
            restaurant.postcodes,
            restaurant.delivery.radius,
            restaurant.general ? restaurant.general.latitude : "",
            restaurant.general ? restaurant.general.longitude : ""
          );
          
          // Always set the delivery info with distance, regardless of delivery status
          newDeliveryInfo[restaurantId] = {
            canDeliver: validation.canDeliver,
            distance: validation.distance,
            nearestLocation: validation.nearestLocation
          };
          
          if (validation.canDeliver) {
            anyRestaurantCanDeliver = true;
          }
        } catch (error) {
          console.error(`Error calculating delivery time for restaurant ${restaurantId}:`, error);
          newDeliveryInfo[restaurantId] = {
            canDeliver: false,
            nearestLocation: restaurant.postcodes[0]
          };
        }
      }
  
      setIsAreaServed(anyRestaurantCanDeliver);
      setRestaurantDeliveryInfo(newDeliveryInfo);
    };
  
    calculateRestaurantDeliveryTimes();
  }, [postcode, restaurants]);

  const handlePostcodeSave = async (newPostcode: string) => {
    setPostcode(newPostcode);
    
    // Always save the postcode to localStorage, regardless of delivery status
    localStorage.setItem('userPostcode', newPostcode);
    
    // Calculate delivery info for all restaurants
    const postcodeService =  PostcodeServiceFactory.getPostcodeService();
    const newDeliveryInfo: RestaurantDeliveryInfo = {};
    let canDeliverToArea = false;
  
    for (const [restaurantId, restaurant] of Object.entries(restaurants)) {
      try {
        const validation = await postcodeService.validateDeliveryLocation(
          newPostcode,
          restaurant.postcodes,
          restaurant.general ? restaurant.general.latitude : "",
          restaurant.general ? restaurant.general.longitude : ""
        );
        
        newDeliveryInfo[restaurantId] = {
          canDeliver: validation.canDeliver,
          distance: validation.distance,
          nearestLocation: validation.nearestLocation
        };
        
        if (validation.canDeliver) {
          canDeliverToArea = true;
        }
      } catch (error) {
        console.error(`Error calculating delivery info for restaurant ${restaurantId}:`, error);
        newDeliveryInfo[restaurantId] = {
          canDeliver: false,
          nearestLocation: restaurant.postcodes[0]
        };
      }
    }
  
    setIsAreaServed(canDeliverToArea);
    setRestaurantDeliveryInfo(newDeliveryInfo);
    setShowPostcodeModal(false);
  };

  const handleRestaurantClick = (restaurantId: string) => {
    navigate(`/order?r=${restaurantId}`);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value.toLowerCase());
    setSelectedCuisine('');
  };

  const handleCuisineClick = (cuisine: string) => {
    setSelectedCuisine(cuisine === selectedCuisine ? '' : cuisine);
    setSearchTerm('');
  };

  const calculateDeliveryTime = (
    restaurant: Restaurant,
    distance: number,
    defaultEstimatedTime: number | undefined
  ): number | undefined => {
    if (restaurant.delivery?.estimationRule) {
      console.log("estimation rule exists")
      const { cookingTime, minsPerMile, extraTime } = restaurant.delivery.estimationRule;
      const travelTime = Math.ceil(distance * minsPerMile);
      return cookingTime + travelTime + extraTime;
    }
    
    return defaultEstimatedTime;
  };

  const renderServiceTags = (restaurant: Restaurant, deliveryInfo: DeliveryInfo | undefined) => {
    const tags: JSX.Element[] = [];
  
    // Check for delivery
    if (restaurant.delivery?.enabled) {
      const isTooFar = deliveryInfo && !deliveryInfo.canDeliver;
      const textColor = isTooFar ? '#DC2626' : '#2E7D32';
      tags.push(
        <span
          key="delivery"
          style={{
            backgroundColor: isTooFar ? '#FEE2E2' : '#E8F5E9',
            color: textColor,
            padding: '4px 8px',
            borderRadius: '999px',
            fontSize: '0.75rem',
            fontWeight: '500',
            border: `1px solid ${textColor}`
          }}
        >
          {isTooFar ? 'Too far to deliver' : 'Delivery'}
        </span>
      );
    }
  
    // Check for pickup
    if (restaurant.pickup?.enabled) {
      const textColor = '#1565C0';
      tags.push(
        <span
          key="pickup"
          style={{
            backgroundColor: '#E3F2FD',
            color: textColor,
            padding: '4px 8px',
            borderRadius: '999px',
            fontSize: '0.75rem',
            fontWeight: '500',
            border: `1px solid ${textColor}`
          }}
        >
          Pick-up
        </span>
      );
    }
  
    // Check for dine-in
    if (restaurant.dinein?.enabled) {
      const textColor = '#7B1FA2';
      tags.push(
        <span
          key="dinein"
          style={{
            backgroundColor: '#F3E5F5',
            color: textColor,
            padding: '4px 8px',
            borderRadius: '999px',
            fontSize: '0.75rem',
            fontWeight: '500',
            border: `1px solid ${textColor}`
          }}
        >
          Dine-in
        </span>
      );
    }
  
    if (tags.length === 0) return null;
  
    return (
      <div style={{
        display: 'flex',
        gap: '8px',
        marginTop: '8px',
        flexWrap: 'wrap'
      }}>
        {tags}
      </div>
    );
  };

  const renderDeliveryInfo = (restaurantId: string) => {
    const deliveryInfo = restaurantDeliveryInfo[restaurantId];
    const restaurant = restaurants[restaurantId];
    
    if (!deliveryInfo) {
      return <span><HiLocationMarker size={14} /> Calculating distance...</span>;
    }
    
    // Handle potentially undefined distance
    const distance = deliveryInfo.distance ?? 0;
    const roundedDistance = Math.round(distance * 10) / 10;
    
    // Check if we should show delivery time
    const showDeliveryTime = 
      restaurant?.delivery?.showEstimatedTime && // Restaurant allows showing time
      deliveryInfo.canDeliver; // Not too far for delivery
      
    // Calculate delivery time if needed
    let estimatedTime: number | undefined;
    if (showDeliveryTime) {
      estimatedTime = calculateDeliveryTime(
        restaurant,
        distance,
        deliveryInfo.estimatedTime
      );
      console.log("111 est", restaurant, distance, estimatedTime)
    }
      
    return (
      <div className="restaurant-meta" style={{
        display: "flex",
        alignItems: "center",
        gap: "15px",
        fontSize: "0.9rem",
        opacity: "0.8"
      }}>
        <span>
          <HiLocationMarker size={14} /> {roundedDistance} miles
        </span>
        {showDeliveryTime && deliveryInfo.canDeliver && restaurant.delivery.enabled && estimatedTime && estimatedTime > 0 && (
          <span>
            <HiTruck size={14} /> {estimatedTime}-{estimatedTime + 15} min
          </span>
        )}
      </div>
    );
};

// Filter and sort restaurants based on search, cuisine, and distance
const getFilteredAndSortedRestaurants = () => {
    const filtered = Object.entries(availableRestaurants).filter(([_, restaurant]) => {
      const searchableText = [
        restaurant.name.toLowerCase(),
        (restaurant.general ? restaurant.general.searchTerms : '').toLowerCase()
      ].join(' ');

      const matchesSearch = searchTerm ? searchableText.includes(searchTerm) : true;
      const matchesCuisine = selectedCuisine ? searchableText.includes(selectedCuisine.toLowerCase()) : true;
      
      return matchesSearch && matchesCuisine;
    });

    // Sort by distance
    return filtered.sort(([idA], [idB]) => {
      const infoA = restaurantDeliveryInfo[idA];
      const infoB = restaurantDeliveryInfo[idB];

      // Handle cases where delivery info isn't available yet
      if (!infoA || !infoB) return 0;
      
      return (infoA.distance || 0) - (infoB.distance || 0);
    });
  };

  const generateRandomColor = (restaurantName: string) => {
    let hash = 0;
    for (let i = 0; i < restaurantName.length; i++) {
      hash = restaurantName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 80%)`; // Light pastel color
  };

  // Function to render restaurant image or placeholder
  const renderRestaurantImage = (restaurant: Restaurant, size: 'small' | 'large' = 'small') => {
    
    if (disableThumbnails) {
      return null;
    }

    if (!(restaurant.general && restaurant.general.thumbnailUrl)) {
      const backgroundColor = generateRandomColor(restaurant.name);
      return (
        <div style={{
          backgroundColor,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <GiKnifeFork
            size={size === 'large' ? 80 : 50}
            style={{
              opacity: 0.3,
              color: '#000'
            }}
          />
        </div>
      );
    }

    return (
      <img
        src={restaurant.general ? restaurant.general.thumbnailUrl : ""}
        alt={restaurant.name}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
      />
    );
  };


  // Update featured restaurants section
  const renderFeaturedCard = ([id, restaurant]: [string, Restaurant]) => {
    return (
      <div
        key={id}
        className="featured-card"
        onClick={() => handleRestaurantClick(id)}
        style={{
          backgroundColor: theme.cardColor, // theme.cartBackgroundColor,
          borderRadius: '12px',
          overflow: 'hidden',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: disableThumbnails ? 'row' : 'column'
        }}
      >
        {!disableThumbnails && (
          <div className="featured-image" style={{ height: '200px' }}>
            {renderRestaurantImage(restaurant, 'large')}
          </div>
        )}
        <div className="featured-info" style={{
          padding: '15px',
          flex: 1
        }}>
          <h3>{restaurant.name}</h3>
          <div className="restaurant-meta">
            {renderDeliveryInfo(id)}
          </div>
          {renderServiceTags(restaurant, restaurantDeliveryInfo[id])}
        </div>
      </div>
    );
  };

  // Update restaurant card in the grid
  const renderRestaurantCard = ([id, restaurant]: [string, Restaurant]) => {
    return (
      <div
        key={id}
        className="restaurant-card"
        onClick={() => handleRestaurantClick(id)}
        style={{
          backgroundColor: theme.cardColor, //theme.cartBackgroundColor,
          borderRadius: '12px',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          border: '1px solid rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: disableThumbnails ? 'row' : 'column'
        }}
      >
        {!disableThumbnails && (
          <div className="restaurant-image" style={{ height: '160px' }}>
            {renderRestaurantImage(restaurant)}
          </div>
        )}
        <div className="restaurant-info" style={{
          padding: '15px',
          flex: 1
        }}>
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: '1.1rem',
            fontWeight: '500'
          }}>
            {restaurant.name}
          </h3>
          <div className="restaurant-meta" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            fontSize: '0.9rem',
            opacity: '0.8'
          }}>
            {renderDeliveryInfo(id)}
          </div>
          {renderServiceTags(restaurant, restaurantDeliveryInfo[id])}
        </div>
      </div>
    );
  };

  const checkRestaurantAvailable = async (restaurantData: any) => {

    //error case - remove this later
    if (!restaurantData.delivery.timings) {
      return false;
    }

    let hoursArray: any[] = [];

    if (restaurantData.delivery.enabled) {
      hoursArray.push(restaurantData.delivery.timings)
    }
    if (restaurantData.pickup.enabled) {
      hoursArray.push(restaurantData.pickup.timings)
    }
    if (restaurantData.dinein.enabled) {
      hoursArray.push(restaurantData.dinein.timings)
    }

    if (hoursArray.length === 0) {
      return false;
    }
    else {
      return await TimeService.checkStoreIsOpen(hoursArray);
    }
  }

  const filteredAndSortedRestaurants = getFilteredAndSortedRestaurants();
  const filteredFeatured = featuredStores.map((storeId: any) => 
    filteredAndSortedRestaurants.find(restaurant => restaurant[0] === storeId)).filter((x: any) => x !== undefined);
  const featuredRestaurants = filteredFeatured.length > 0 ? filteredFeatured : filteredAndSortedRestaurants.slice(0, 4);
  const showCategoriesSection = !searchTerm;
  const showFeaturedSection = !searchTerm && !selectedCuisine;

  const searchPlaceholder = config?.properties?.searchHomePlaceholder || "Search...";

  const isMobile = windowWidth <= 999;

  return (
    <div style={{display: "flex", width: "100%", height: "100%", overflowX: "hidden", overflowY: "hidden"}}>
      <PostcodeModal
        isOpen={showPostcodeModal}
        onClose={() => setShowPostcodeModal(false)}
        onSave={handlePostcodeSave}
        initialPostcode={postcode}
        isAreaServed={isAreaServed}
      />
      
      <div className="menu-web-container main-content" style={{
        display: "flex",
        flexDirection: "column",
        height: "100%", 
        color: theme.textColor, 
        backgroundColor: theme.topbarBackgroundColor,
        flexGrow: 1,
        overflowX: "hidden",
      }}>
        {/* Header Section */}
        <div className="header-section" style={{
          backgroundColor: window.innerWidth <= 999 ? theme.topbarBackgroundColor : theme.backgroundColor,
          color: window.innerWidth <= 999 ? theme.topbarIconColor : theme.textColor,
          borderBottom: "2px solid " + theme.borderColor,
          padding: "20px",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}>
          <div className="location-bar" style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "15px"
          }}>
            <LocationBar 
              postcode={postcode} 
              onEdit={() => setShowPostcodeModal(true)} 
            />
          </div>
          
          <div className="search-container" style={{
            backgroundColor: theme.searchBarColor,
            border: "1px solid " + theme.borderColor,
            borderRadius: "8px",
            padding: "10px 15px",
            display: "flex",
            alignItems: "center",
          }}>
            <HiSearch size={20} color={theme.textColor} />
            <input 
              type="text" 
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearch}
              style={{
                flex: 1,
                border: "none",
                marginLeft: "10px",
                fontSize: "1rem",
                backgroundColor: theme.searchBarColor || "transparent",
                color: theme.textColor
              }}
            />
          </div>
        </div>

          <div style={{backgroundColor: theme.backgroundColor, paddingTop: '20px', boxSizing: "border-box", flexGrow: 1}}>

            {/* Categories Section */}
            {showCategoriesSection && showCategories && (
              <div className="categories-section" style={{padding: "0px 0 10px 0", backgroundColor: theme.backgroundColor}}>
                <div className="scroll-container categories-scroll">
                  {CUISINE_TYPES.map((cuisine, index) => (
                    <div 
                      key={index} 
                      className={`category-item ${selectedCuisine === cuisine.name ? 'selected' : ''}`}
                      onClick={() => handleCuisineClick(cuisine.name)}
                      style={{
                        backgroundColor: theme.cardColor, // theme.cartBackgroundColor,
                        borderRadius: "12px",
                        padding: "15px",
                        minWidth: isMobile ? "60px" : "100px",
                        textAlign: "center",
                        cursor: "pointer",
                        border: selectedCuisine === cuisine.name ? `2px solid ${cuisine.color}` : '1px solid transparent'
                      }}
                    >
                      <div style={{
                        backgroundColor: cuisine.color,
                        borderRadius: "50%",
                        width: isMobile ? "25px" : "50px",
                        height: isMobile ? "25px" : "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 10px"
                      }}>
                        {React.createElement(cuisine.icon, { 
                          size: isMobile ? 15 : 30, 
                          color: "white"
                        })}
                      </div>
                      <span style={{fontSize: "0.9rem", fontWeight: "500"}}>{cuisine.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Featured Restaurants Section */}
            {showFeaturedSection && showFeatured && (
                <div className="section" style={{marginTop: showCategories ? '10px': '50px', backgroundColor: theme.backgroundColor}}>
                <div className="section-header">
                    <h2>Featured Restaurants</h2>
                </div>
                <div className="scroll-container featured-scroll">
                    {featuredRestaurants.filter((restaurantEntry: any) => checkRestaurantAvailable(restaurantEntry[1])).map((restaurantEntry: any) => renderFeaturedCard(restaurantEntry))}
                </div>
                </div>
            )}

            {/* All Restaurants Section */}
            <div className="section" style={{marginTop: '20px', backgroundColor: theme.backgroundColor}}>
                <div className="section-header">
                <h2>
                    {searchTerm || selectedCuisine ? 'Search Results' : 'All Restaurants'}
                </h2>
                {postcode && (
                    <span style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                    Sorted by distance
                    </span>
                )}
                </div>
                <div className="restaurants-grid" style={{paddingBottom: "50px"}}>
                {filteredAndSortedRestaurants.map((restaurantEntry: any) => renderRestaurantCard(restaurantEntry))}
                </div>
            </div>





          </div>

      </div>

    </div>
  );
};

export default RestaurantsPage;